import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  BanknotesIcon,
  PlayIcon,
  PauseIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  useVendorsPage,
  useVendorSummary,
  useVendorPurchaseSummary,
  useCreateVendor,
  useUpdateVendor,
  useDeleteVendor,
} from '../../hooks/useVendors';
import { CreateVendorRequest, VendorListItem, VendorSortField } from '../../types/vendor.types';
import { INDIAN_STATES } from '../../types/customer.types';
import VendorForm from '../../components/forms/VendorForm';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  ConfirmDialog,
  DetailCard,
  DetailItem,
  FormActions,
  badgeClass,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { defaultRowsPerPage } from '../../lib/preferences';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const SORT_OPTIONS: { value: string; label: string; sortBy: VendorSortField; sortOrder: 'ASC' | 'DESC' }[] = [
  { value: 'name', label: 'Name A–Z', sortBy: 'name', sortOrder: 'ASC' },
  { value: 'code', label: 'Code A–Z', sortBy: 'code', sortOrder: 'ASC' },
  { value: 'paymentTerms', label: 'Longest payment terms', sortBy: 'paymentTerms', sortOrder: 'DESC' },
  { value: 'createdAt', label: 'Newest first', sortBy: 'createdAt', sortOrder: 'DESC' },
];

const ORDER_STATUS_META: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'text-gray-700 bg-gray-50 ring-gray-500/20' },
  approved: { label: 'Approved', color: 'text-blue-700 bg-blue-50 ring-blue-600/20' },
  received: { label: 'Received', color: 'text-green-700 bg-green-50 ring-green-600/20' },
  cancelled: { label: 'Cancelled', color: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20' },
};

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

const formatTerms = (days: number) => (days > 0 ? `${days} days` : 'On delivery');

const VendorList: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>('');
  const [stateFilter, setStateFilter] = useState('');
  const [sort, setSort] = useState(SORT_OPTIONS[0].value);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultRowsPerPage);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<VendorListItem | null>(null);
  const [viewVendor, setViewVendor] = useState<VendorListItem | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VendorListItem | null>(null);
  const [toggleTarget, setToggleTarget] = useState<VendorListItem | null>(null);

  // Debounce search so the API is not called on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const sortOption = SORT_OPTIONS.find((option) => option.value === sort) || SORT_OPTIONS[0];
  const { data: vendorPage, isLoading, isFetching, error } = useVendorsPage({
    page,
    limit: pageSize,
    search: debouncedSearch || undefined,
    state: stateFilter || undefined,
    isActive: statusFilter ? statusFilter === 'active' : undefined,
    sortBy: sortOption.sortBy,
    sortOrder: sortOption.sortOrder,
  });
  const { data: summary, isLoading: isSummaryLoading } = useVendorSummary();
  const { data: purchases, isLoading: isPurchasesLoading } = useVendorPurchaseSummary(
    isViewOpen && viewVendor ? viewVendor.id : null,
  );
  const createMutation = useCreateVendor();
  const updateMutation = useUpdateVendor();
  const deleteMutation = useDeleteVendor();

  const vendors = vendorPage?.data ?? [];
  const total = vendorPage?.total ?? 0;
  const totalPages = vendorPage?.totalPages ?? 1;
  const isSaving = createMutation.isPending || (updateMutation.isPending && isFormOpen);

  // Step back when the current page no longer exists
  useEffect(() => {
    if (vendorPage && page > 1 && page > vendorPage.totalPages) {
      setPage(Math.max(1, vendorPage.totalPages));
    }
  }, [vendorPage, page]);

  const openCreate = () => {
    setEditingVendor(null);
    setIsFormOpen(true);
  };

  const openEdit = (vendor: VendorListItem) => {
    setEditingVendor(vendor);
    setIsFormOpen(true);
  };

  const openView = (vendor: VendorListItem) => {
    setViewVendor(vendor);
    setIsViewOpen(true);
  };

  const handleSubmit = async (data: CreateVendorRequest) => {
    try {
      if (editingVendor) {
        await updateMutation.mutateAsync({ id: editingVendor.id, data });
        toast.success('Vendor updated successfully');
      } else {
        await createMutation.mutateAsync(data);
        toast.success('Vendor created successfully');
        setPage(1);
      }
      setIsFormOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, editingVendor ? 'Could not update the vendor' : 'Could not create the vendor'));
      throw err;
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Vendor deleted successfully');
      setDeleteTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not delete the vendor'));
    }
  };

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    try {
      await updateMutation.mutateAsync({ id: toggleTarget.id, data: { isActive: !toggleTarget.isActive } });
      toast.success(toggleTarget.isActive ? 'Vendor marked inactive' : 'Vendor reactivated');
      setToggleTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not change the vendor status'));
    }
  };

  const hasFilters = !!(search || statusFilter || stateFilter);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setStateFilter('');
    setPage(1);
  };

  const stats = [
    {
      label: 'Total Vendors',
      value: formatNumber(summary?.total ?? 0),
      hint: `${formatNumber(summary?.inactive ?? 0)} inactive`,
      icon: BuildingOfficeIcon,
      tone: 'text-indigo-600 bg-indigo-50',
    },
    {
      label: 'Active',
      value: formatNumber(summary?.active ?? 0),
      hint: 'Can be put on new purchase orders',
      icon: CheckCircleIcon,
      tone: 'text-green-600 bg-green-50',
    },
    {
      label: 'On Order',
      value: formatMoney(summary?.openValue ?? 0),
      hint: `${formatNumber(summary?.openOrders ?? 0)} open ${summary?.openOrders === 1 ? 'order' : 'orders'} with ${formatNumber(summary?.withOpenOrders ?? 0)} ${summary?.withOpenOrders === 1 ? 'vendor' : 'vendors'}`,
      icon: ClipboardDocumentListIcon,
      tone: 'text-blue-600 bg-blue-50',
    },
    {
      label: 'Purchased',
      value: formatMoney(summary?.receivedValue ?? 0),
      hint: 'Value of the orders received so far',
      icon: BanknotesIcon,
      tone: 'text-teal-600 bg-teal-50',
    },
  ];

  const selectClass =
    'h-10 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
  const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
  const iconButtonClass = 'p-1.5 rounded disabled:opacity-50';

  const renderStatus = (vendor: { isActive: boolean }) => (
    <span
      className={`${badgeClass} ${
        vendor.isActive ? 'text-green-700 bg-green-50 ring-green-600/20' : 'text-gray-700 bg-gray-50 ring-gray-500/20'
      }`}
    >
      {vendor.isActive ? 'Active' : 'Inactive'}
    </span>
  );

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading vendors</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Vendors</h1>
          <p className="mt-1 text-sm text-gray-600">Manage supplier details, GST registration and payment terms</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Vendor
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200"
          >
            <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${stat.tone}`}>
              <stat.icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="truncate text-xl font-semibold text-gray-900">{isSummaryLoading ? '—' : stat.value}</p>
              <p className="truncate text-xs text-gray-500">{isSummaryLoading ? ' ' : stat.hint}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {/* Search & filters */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, code, email, phone, contact, city or GST number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block h-10 w-full rounded-md border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as 'active' | 'inactive' | '');
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              aria-label="Filter by state"
              value={stateFilter}
              onChange={(e) => {
                setStateFilter(e.target.value);
                setPage(1);
              }}
              className={`${selectClass} max-w-[11rem]`}
            >
              <option value="">All states</option>
              {INDIAN_STATES.map((state) => (
                <option key={state.gstCode} value={state.name}>
                  {state.name}
                </option>
              ))}
            </select>
            <select
              aria-label="Sort vendors"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setPage(1);
              }}
              className={selectClass}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex h-10 items-center rounded-md px-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <XMarkIcon className="h-4 w-4 mr-1" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Vendors table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className={thClass}>Vendor</th>
                <th className={thClass}>Contact</th>
                <th className={thClass}>Location</th>
                <th className={thClass}>GST Number</th>
                <th className={thClass}>Payment Terms</th>
                <th className={`${thClass} text-right`}>Purchased</th>
                <th className={thClass}>Status</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-gray-100 bg-white ${isFetching && !isLoading ? 'opacity-60' : ''}`}>
              {isLoading ? (
                <tr>
                  <td className="px-4 py-12 text-center text-sm text-gray-500" colSpan={8}>
                    Loading vendors...
                  </td>
                </tr>
              ) : vendors.length === 0 ? (
                <tr>
                  <td className="px-4 py-12 text-center" colSpan={8}>
                    <BuildingOfficeIcon className="mx-auto h-10 w-10 text-gray-300" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No vendors found</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {hasFilters
                        ? 'Try changing or clearing the search and filters.'
                        : 'Add a vendor to start raising purchase orders.'}
                    </p>
                  </td>
                </tr>
              ) : (
                vendors.map((vendor) => (
                  <tr key={vendor.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{vendor.name}</div>
                      <div>
                        <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">{vendor.code}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="text-gray-900">{vendor.contactPerson || '—'}</div>
                      <div className="text-xs text-gray-500">{vendor.contactPhone || vendor.phone}</div>
                      <div className="text-xs text-gray-500">{vendor.email}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      <div>{vendor.city}</div>
                      <div className="text-xs text-gray-500">{vendor.state}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">
                      {vendor.gstNumber ? (
                        <span className="font-mono text-xs text-gray-700">{vendor.gstNumber}</span>
                      ) : (
                        <span className="text-xs text-gray-500">Unregistered</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{formatTerms(vendor.paymentTerms)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                      <div className="font-medium text-gray-900 tabular-nums">{formatMoney(vendor.receivedValue)}</div>
                      <div className="text-xs text-gray-500">
                        {vendor.orderCount === 0
                          ? 'No orders yet'
                          : `${formatNumber(vendor.orderCount)} ${vendor.orderCount === 1 ? 'order' : 'orders'}${
                              vendor.openOrders > 0 ? `, ${formatNumber(vendor.openOrders)} open` : ''
                            }`}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm">{renderStatus(vendor)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => setToggleTarget(vendor)}
                          className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${
                            vendor.isActive
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-green-50 text-green-700 hover:bg-green-100'
                          }`}
                        >
                          {vendor.isActive ? (
                            <PauseIcon className="h-3.5 w-3.5 mr-1" />
                          ) : (
                            <PlayIcon className="h-3.5 w-3.5 mr-1" />
                          )}
                          {vendor.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => openView(vendor)}
                          className={`${iconButtonClass} text-gray-600 hover:bg-gray-100 hover:text-gray-900`}
                          title="View Details"
                          aria-label="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => openEdit(vendor)}
                          className={`${iconButtonClass} text-indigo-600 hover:bg-indigo-50 hover:text-indigo-900`}
                          title="Edit Vendor"
                          aria-label="Edit Vendor"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(vendor)}
                          className={`${iconButtonClass} text-red-600 hover:bg-red-50 hover:text-red-900`}
                          title="Delete Vendor"
                          aria-label="Delete Vendor"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!isLoading && total > 0 && (
          <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:gap-6">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500">
              <span>
                Showing {formatNumber((page - 1) * pageSize + 1)}–
                {formatNumber(Math.min(page * pageSize, total))} of {formatNumber(total)} vendors
              </span>
              <label className="flex items-center gap-2">
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <Pagination className="lg:flex-1" currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* Create or edit */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => !isSaving && setIsFormOpen(false)}
        title={editingVendor ? 'Edit Vendor' : 'Add Vendor'}
        description={
          editingVendor
            ? `${editingVendor.name} · ${editingVendor.code}`
            : 'Vendors can be put on purchase orders once they are saved.'
        }
        size="2xl"
        className="overflow-visible"
      >
        <VendorForm
          key={editingVendor ? `${editingVendor.id}-${editingVendor.updatedAt}` : 'new'}
          vendor={editingVendor}
          onSubmit={handleSubmit}
          onCancel={() => setIsFormOpen(false)}
          isSaving={isSaving}
        />
      </Modal>

      {/* Details */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Vendor Details" size="xl">
        {viewVendor && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p>
                  <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">{viewVendor.code}</span>
                </p>
                <h4 className="mt-2 text-lg font-semibold text-gray-900">{viewVendor.name}</h4>
                <p className="mt-1 text-sm text-gray-600">
                  {viewVendor.city}, {viewVendor.state} · vendor since {formatDate(viewVendor.createdAt)}
                </p>
              </div>
              {renderStatus(viewVendor)}
            </div>

            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[
                {
                  label: 'Purchased',
                  value: formatMoney(purchases?.receivedValue ?? viewVendor.receivedValue),
                  tone: 'bg-indigo-50 text-indigo-900',
                },
                {
                  label: 'On order',
                  value: formatMoney(purchases?.openValue ?? viewVendor.openValue),
                  tone: 'bg-blue-50 text-blue-900',
                },
                {
                  label: 'Purchase orders',
                  value: formatNumber(purchases?.orderCount ?? viewVendor.orderCount),
                  tone: 'bg-gray-50 text-gray-900',
                },
                {
                  label: 'Last order',
                  value: formatDate(purchases?.lastOrderDate ?? viewVendor.lastOrderDate),
                  tone: 'bg-gray-50 text-gray-900',
                },
              ].map((item) => (
                <div key={item.label} className={`rounded-lg px-4 py-3 ${item.tone}`}>
                  <p className="text-xs font-medium uppercase tracking-wide opacity-70">{item.label}</p>
                  <p className="mt-1 text-lg font-semibold tabular-nums">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <DetailCard title="Contact">
                <DetailItem label="Contact person">{viewVendor.contactPerson || '—'}</DetailItem>
                <DetailItem label="Contact phone">{viewVendor.contactPhone || '—'}</DetailItem>
                <DetailItem label="Email" className="break-all">
                  {viewVendor.email}
                </DetailItem>
                <DetailItem label="Phone">{viewVendor.phone}</DetailItem>
              </DetailCard>

              <DetailCard title="Tax and payment">
                <DetailItem label="GST number">
                  {viewVendor.gstNumber ? <span className="font-mono">{viewVendor.gstNumber}</span> : 'Unregistered'}
                </DetailItem>
                <DetailItem label="PAN number">
                  {viewVendor.panNumber ? <span className="font-mono">{viewVendor.panNumber}</span> : '—'}
                </DetailItem>
                <DetailItem label="Payment terms">{formatTerms(viewVendor.paymentTerms)}</DetailItem>
                <DetailItem label="Last updated">{formatDate(viewVendor.updatedAt)}</DetailItem>
              </DetailCard>
            </div>

            <DetailCard title="Address">
              <dd className="col-span-2 text-sm text-gray-900">
                {viewVendor.address}
                <br />
                {viewVendor.city}, {viewVendor.state} {viewVendor.pincode}
              </dd>
            </DetailCard>

            <section className="overflow-hidden rounded-lg border border-gray-200">
              <h4 className="border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Latest purchase orders
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-white">
                    <tr>
                      <th className={thClass}>Order</th>
                      <th className={thClass}>Status</th>
                      <th className={thClass}>Order Date</th>
                      <th className={thClass}>Expected</th>
                      <th className={`${thClass} text-right`}>Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {isPurchasesLoading ? (
                      <tr>
                        <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={5}>
                          Loading orders...
                        </td>
                      </tr>
                    ) : !purchases || purchases.recentOrders.length === 0 ? (
                      <tr>
                        <td className="px-4 py-6 text-center text-sm text-gray-500" colSpan={5}>
                          Nothing has been ordered from this vendor yet
                        </td>
                      </tr>
                    ) : (
                      purchases.recentOrders.map((order) => {
                        const meta = ORDER_STATUS_META[order.status] || { label: order.status, color: ORDER_STATUS_META.draft.color };
                        return (
                          <tr key={order.id}>
                            <td className="whitespace-nowrap px-4 py-2.5 text-sm">
                              <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                                {order.orderNumber}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-2.5 text-sm">
                              <span className={`${badgeClass} ${meta.color}`}>{meta.label}</span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-700">{formatDate(order.orderDate)}</td>
                            <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-700">{formatDate(order.expectedDate)}</td>
                            <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900 tabular-nums">
                              {formatMoney(order.grandTotal)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {purchases && purchases.orderCount > purchases.recentOrders.length && (
                <p className="border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-500">
                  Showing the latest {purchases.recentOrders.length}; all orders are on the Purchase page.
                </p>
              )}
            </section>
          </div>
        )}

        <FormActions>
          <button type="button" onClick={() => setIsViewOpen(false)} className={secondaryButtonClass}>
            Close
          </button>
          {viewVendor && (
            <button
              type="button"
              onClick={() => {
                setIsViewOpen(false);
                openEdit(viewVendor);
              }}
              className={primaryButtonClass}
            >
              <PencilIcon className="mr-2 h-4 w-4" />
              Edit Vendor
            </button>
          )}
        </FormActions>
      </Modal>

      {/* Activate / deactivate confirmation */}
      <ConfirmDialog
        isOpen={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={confirmToggle}
        title={toggleTarget?.isActive ? 'Deactivate Vendor' : 'Activate Vendor'}
        message={
          <>
            <span className="font-medium text-gray-900">{toggleTarget?.name}</span>{' '}
            {toggleTarget?.isActive
              ? 'will no longer be available on new purchase orders. Existing orders are not changed.'
              : 'will be available on new purchase orders again.'}
          </>
        }
        confirmLabel={toggleTarget?.isActive ? 'Deactivate' : 'Activate'}
        pendingLabel="Updating..."
        isPending={updateMutation.isPending}
        tone="primary"
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete Vendor"
        message={
          <>
            <span className="font-medium text-gray-900">{deleteTarget?.name}</span> will be permanently deleted. This
            cannot be undone. A vendor that already has purchase orders cannot be deleted and should be deactivated
            instead.
          </>
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        isPending={deleteMutation.isPending}
      />
    </div>
  );
};

export default VendorList;
